/**
 * AdminMeTemplatePage - Slice B² rewrite (Iter 9.40+).
 *
 * THIS IS THE /me PAGE EDITOR. It mirrors the live /me page that every
 * authenticated user sees (Dashboard · Preferences · Inspo · Experiences · Settings).
 * NOT the host dashboard editor (that lives under Host Dashboards in the sidebar).
 *
 * Each tab is a click-to-edit wireframe. Section titles, copy and the
 * data sources (which master DB each section pulls from) are editable.
 * Persists per-tab to db.admin_settings.me_template under
 * `tabs.{tab_key}.{section}`.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2,
  Save,
  Eye,
  ArrowUp,
  Edit3,
  Home,
  Heart,
  Camera,
  Sparkles,
  Settings as SettingsIcon,
  Database
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const ME_TABS = [
  { key: "dashboard",   label: "Dashboard",   icon: Home },
  { key: "preferences", label: "Preferences", icon: Heart },
  { key: "inspo",       label: "Inspo",       icon: Camera },
  { key: "experiences", label: "Experiences", icon: Sparkles },
  { key: "settings",    label: "Settings",    icon: SettingsIcon },
];

// Default per-tab template - every section declares its master DB source so the
// admin can see (and trust) where the data on /me actually comes from.
const DEFAULT_TPL = {
  dashboard: {
    enabled: true,
    title: "Welcome back",
    subtitle: "Your preferences. Your experiences.",
    show_upcoming: true,
    show_hosting: true,
    show_inner_circle: true,
    source: "db.users + db.events",
  },
  preferences: {
    enabled: true,
    title: "Preferences",
    subtitle: "Saved cards by category - synced across every host you've shared with.",
    show_categories_grid: true,
    show_share_each: true,
    show_link_to_host: true,
    source: "db.categories.preference_fields + db.user_preferences",
  },
  inspo: {
    enabled: true,
    title: "Inspiration",
    subtitle: "Save looks per category folder.",
    show_default_folders: true,
    allow_custom_folders: true,
    source: "db.master_inspo + db.user_inspo",
  },
  experiences: {
    enabled: true,
    title: "Experiences",
    subtitle: "Plan a service or an occasion bundle.",
    show_upcoming: true,
    show_past: true,
    show_plan_service_cta: true,
    show_plan_occasion_cta: true,
    source: "db.master_experiences + db.master_occasions + db.user_experiences",
  },
  settings: {
    enabled: true,
    title: "Settings",
    subtitle: "Profile, integrations, billing.",
    show_profile: true,
    show_integrations: true,
    show_billing: true,
    show_referral: true,
    source: "db.users + db.user_integrations",
  },
};

export default function AdminMeTemplatePage({ embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState({ tabs: {} });
  const [editing, setEditing] = useState(null); // { tab_key }

  const tabs = useMemo(() => {
    const merged = {};
    ME_TABS.forEach(({ key }) => {
      merged[key] = { ...DEFAULT_TPL[key], ...((doc.tabs || {})[key] || {}) };
    });
    return merged;
  }, [doc]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/me-template`, { headers: auth() });
      if (r.ok) {
        const d = await r.json();
        setDoc(d.template || { tabs: {} });
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateTab = (tabKey, patch) => {
    setDoc(d => ({
      ...d,
      tabs: { ...(d.tabs || {}), [tabKey]: { ...((d.tabs || {})[tabKey] || {}), ...patch } },
    }));
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/me-template`, {
        method: "PUT",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ tabs: doc.tabs || {} }),
      });
      if (r.ok) { toast.success("/me Template saved - applies to every user's /me page"); load(); }
      else toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20" data-testid="me-template-loading"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className={embedded ? "" : "p-4 sm:p-6 max-w-6xl mx-auto"} data-testid="admin-me-template-page">
      {/* Sticky header */}
      <div className="bg-white border border-stone-200 rounded-2xl mb-4 p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">/me Template Editor</h1>
          <p className="text-xs text-stone-500 mt-0.5">This is the live /me page every user sees. Click any section to edit. All data is pulled from master databases.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-100 text-blue-800 border-0 font-semibold">ADMIN MODE</Badge>
          <Button variant="outline" size="sm" onClick={() => window.open("/me", "_blank")} data-testid="me-tpl-open-live">
            <Eye className="w-3.5 h-3.5 mr-1" />Open live /me
          </Button>
          <Button onClick={saveTemplate} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="me-tpl-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save Template
          </Button>
        </div>
      </div>

      {/* Edit hint */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5 mb-4 flex items-center gap-2">
        <Edit3 className="w-3.5 h-3.5 text-blue-700" />
        <p className="text-xs text-blue-800">Click on any section to edit titles, copy, and toggle visibility. Underlying data flows from the listed master DBs.</p>
      </div>

      {/* /me page tabs - exactly mirrors the live /me */}
      <Tabs defaultValue="dashboard" className="mt-2">
        <TabsList className="h-11 bg-stone-100">
          {ME_TABS.map(({ key, label, icon: Icon }) => (
            <TabsTrigger key={key} value={key} data-testid={`me-tpl-tab-${key}`}>
              <Icon className="w-3.5 h-3.5 mr-1.5" />{label}
            </TabsTrigger>
          ))}
        </TabsList>

        {ME_TABS.map(({ key }) => (
          <TabsContent key={key} value={key} className="mt-4">
            <TabWireframe tabKey={key} tpl={tabs[key]} onClick={() => setEditing({ tab_key: key })} />
          </TabsContent>
        ))}
      </Tabs>

      {/* Edit drawer */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl" data-testid="me-tpl-edit-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">Edit {editing?.tab_key} tab</DialogTitle>
          </DialogHeader>
          {editing && <EditDrawer tabKey={editing.tab_key} tpl={tabs[editing.tab_key]} update={(patch) => updateTab(editing.tab_key, patch)} />}
          <div className="flex justify-end pt-3">
            <Button onClick={() => setEditing(null)} variant="outline" size="sm">Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Tab wireframes ──────────────────────────────────────────────────────────

function TabWireframe({ tabKey, tpl, onClick }) {
  return (
    <Card
      onClick={onClick}
      role="button"
      className="cursor-pointer transition hover:ring-2 hover:ring-blue-300 hover:ring-offset-2 rounded-2xl"
      data-testid={`me-tpl-wf-${tabKey}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-bold">{tpl.title}</h2>
            <p className="text-sm text-stone-500">{tpl.subtitle}</p>
          </div>
          <Badge className="bg-stone-100 text-stone-600 border-0 text-[10px] font-mono shrink-0 flex items-center gap-1">
            <Database className="w-3 h-3" />{tpl.source}
          </Badge>
        </div>

        {tabKey === "dashboard" && <DashboardWf tpl={tpl} />}
        {tabKey === "preferences" && <PreferencesWf tpl={tpl} />}
        {tabKey === "inspo" && <InspoWf tpl={tpl} />}
        {tabKey === "experiences" && <ExperiencesWf tpl={tpl} />}
        {tabKey === "settings" && <SettingsWf tpl={tpl} />}
      </CardContent>
    </Card>
  );
}

function DashboardWf({ tpl }) {
  return (
    <div className="space-y-3">
      {tpl.show_upcoming && <Block label="Upcoming" hint="db.events where attendee=current_user" />}
      {tpl.show_hosting && <Block label="Hosting CTA" hint="visible if user.role contains 'host'" />}
      {tpl.show_inner_circle && <Block label="Inner-circle messages" hint="db.messages.inner_circle" />}
    </div>
  );
}

function PreferencesWf({ tpl }) {
  const cats = ["makeup", "hair", "nails", "lashes", "wellness", "culinary", "events", "instructors", "travel", "dispensary", "style"];
  return (
    <div className="space-y-3">
      {tpl.show_categories_grid && (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-stone-500 mb-2">Category cards (pulled from db.categories.preference_fields)</p>
          <div className="flex flex-wrap gap-1.5">
            {cats.map(c => <Badge key={c} variant="outline" className="text-[10px] capitalize">{c}</Badge>)}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
        {tpl.show_share_each && <Badge variant="outline" className="text-xs flex items-center gap-1"><ArrowUp className="w-3 h-3" />Share each card</Badge>}
        {tpl.show_link_to_host && <Badge variant="outline" className="text-xs">Link to host</Badge>}
      </div>
    </div>
  );
}

function InspoWf({ tpl }) {
  return (
    <div className="space-y-3">
      {tpl.show_default_folders && <Block label="Admin default folders" hint="db.master_inspo (per category)" />}
      {tpl.allow_custom_folders && <Block label="User-created folders" hint="db.user_inspo.folders" />}
    </div>
  );
}

function ExperiencesWf({ tpl }) {
  return (
    <div className="space-y-3">
      {tpl.show_upcoming && <Block label="Upcoming experiences" hint="db.user_experiences where date >= today" />}
      {tpl.show_past && <Block label="Past experiences" hint="db.user_experiences history" />}
      <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
        {tpl.show_plan_service_cta && <Badge className="bg-blue-700 text-white text-xs">+ Plan a service</Badge>}
        {tpl.show_plan_occasion_cta && <Badge className="bg-blue-100 text-blue-800 text-xs">+ Plan an occasion</Badge>}
      </div>
    </div>
  );
}

function SettingsWf({ tpl }) {
  return (
    <div className="space-y-2">
      {tpl.show_profile && <Block label="Profile photo · name · email · phone" hint="db.users" />}
      {tpl.show_integrations && <Block label="Integrations (Acuity · Stripe · Beehiiv · Calendar)" hint="db.user_integrations" />}
      {tpl.show_billing && <Block label="Billing & subscription" hint="db.subscriptions (Stripe - host only)" />}
      {tpl.show_referral && <Block label="Referral / advocate code" hint="db.referrals" />}
    </div>
  );
}

function Block({ label, hint }) {
  return (
    <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5">
      <p className="text-xs font-semibold text-stone-800">{label}</p>
      <p className="text-[10px] text-stone-500 font-mono">{hint}</p>
    </div>
  );
}

// ─── Edit drawer ─────────────────────────────────────────────────────────────

function EditDrawer({ tabKey, tpl, update }) {
  const flagKeys = Object.keys(DEFAULT_TPL[tabKey]).filter(k => typeof DEFAULT_TPL[tabKey][k] === "boolean");
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs text-stone-500">Section title</Label>
        <Input value={tpl.title || ""} onChange={(e) => update({ title: e.target.value })} className="mt-1 h-8 text-sm" data-testid={`me-tpl-${tabKey}-title`} />
      </div>
      <div>
        <Label className="text-xs text-stone-500">Subtitle / intro copy</Label>
        <Textarea rows={2} value={tpl.subtitle || ""} onChange={(e) => update({ subtitle: e.target.value })} className="mt-1 text-sm" data-testid={`me-tpl-${tabKey}-subtitle`} />
      </div>
      <div>
        <Label className="text-xs text-stone-500">Master DB source (read-only - drives where data on this tab comes from)</Label>
        <Input value={tpl.source || ""} onChange={(e) => update({ source: e.target.value })} className="mt-1 h-8 text-sm font-mono" data-testid={`me-tpl-${tabKey}-source`} />
      </div>
      <div className="border-t border-stone-100 pt-3">
        <p className="text-xs font-semibold text-stone-700 mb-2">Visible blocks</p>
        <div className="space-y-1">
          {flagKeys.filter(k => k !== "enabled").map(k => (
            <label key={k} className="flex items-center justify-between gap-2 p-2 hover:bg-stone-50 rounded">
              <span className="text-xs text-stone-700 capitalize">{k.replace(/^show_/, "").replace(/_/g, " ")}</span>
              <Switch checked={!!tpl[k]} onCheckedChange={(v) => update({ [k]: v })} data-testid={`me-tpl-${tabKey}-${k}`} />
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
